import { v4 as uuidv4 } from "uuid";

import type { ICreateArticleCmd } from "@/data/commands/catalog/CreateArticleCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError } from "@/data/dao/service/BaseServiceDao";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import { TableDao } from "@/data/dao/service/TableDao";
import type { IArticle, IFullArticle } from "@/data/model/Article";
import type { IAttributeLinkableEntity } from "@/data/model/Attribute";
import { AttributeEffect } from "@/data/model/Enums";
import type { IFulfillmentGroup } from "@/data/model/FulfillmentGroup";
import type { IReportCategory } from "@/data/model/ReportCategory";
import { exceptionTypes } from "@/data/util/Config";



import { translate } from "@/shared/localization/language";
import { logError } from "@/shared/logging/errors";
import { type VueComponentInstance, createVueComponent } from "@/shared/utils/vue";



import { MultiEntityInput } from "@/components/formControls/MultiEntityInput";



import { i18n } from "@/portal/Trans";
import ArticleResourcesForm from "@/portal/components/article/ArticleResourcesForm.vue";
import { ArticleProductionSiteAllocator } from "@/portal/elements/ArticleProductionSiteAllocator";
import { OptionGroupAllocator } from "@/portal/elements/OptionGroupAllocator";
import { ImagePicker } from "@/portal/formControls/ImagePicker";
import { MultiLanguageInput } from "@/portal/formControls/MultiLanguageInput";
import { MultiTextInput } from "@/portal/formControls/MultiTextInput";
import { NumberInput } from "@/portal/formControls/NumberInput";
import { Select } from "@/portal/formControls/Select";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import { BeforeSaveValidation } from "@/portal/helpers/BeforeSaveValidation";
import { DomUtils } from "@/portal/helpers/DomUtils";


export class ArticleForm extends BaseEntityForm<IFullArticle, ICreateArticleCmd> {
    private readonly valueVoucherSalesReportCategoryName: string = "Value voucher sales";

    private availabilitySwitch: Switch;
    private nameField: MultiLanguageInput;
    private shortDescriptionField: MultiLanguageInput;
    private longDescriptionField: MultiLanguageInput;
    private categoryInput: MultiEntityInput;
    private attributeInput: MultiEntityInput;
    private priceInput: NumberInput;
    private oohPriceInput: NumberInput;
    private vatInput: NumberInput;
    private oohVatInput: NumberInput;
    private minAgeInput: NumberInput;
    private minQuantityInput: NumberInput;
    private maxQuantityInput: NumberInput;
    private isOpenPriceEnabledSwitch: Switch;
    private isNegativeOpenPriceAllowedSwitch: Switch;
    private $isNegativeOpenPriceAllowedRow: JQuery;
    private pricePerMinuteSwitch: Switch;
    private timerAutoStartAtOperatorCheckInSwitch: Switch;
    private $timerAutoStartAtOperatorCheckInRow: JQuery;
    private continuationGroupsInput: MultiTextInput;
    private $continuationGroupsRow: JQuery;
    private tablesInput: MultiEntityInput;
    private $tablesRow: JQuery;
    // the chips are filled once the tables are loaded, until then the saved assignment must not get lost
    private tablesLoaded = true;
    private savedTableIds = new Array<string>();
    private optionGroupAllocator: OptionGroupAllocator;
    private canHaveCommentsSwitch: Switch;
    private articleProductionSiteAllocations?: ArticleProductionSiteAllocator;
    private reportCategorySelect?: Select<IReportCategory>;
    private fulfillmentGroupSelect?: Select<IFulfillmentGroup>;
    private imagePicker: ImagePicker<IArticle>;
    // one per form: a duplicate create request returns the article of the first one. A retry after a failed image upload
    // is an update, the form has the id by then
    private readonly idempotencyKey = uuidv4();
    private $internalNameField: JQuery;
    private $internalNoteField: JQuery;
    private $minQuantityField: JQuery;
    private articleResourcesForm: VueComponentInstance<typeof ArticleResourcesForm> | undefined;

    private $voucherValueSection: JQuery;
    private isValueVoucherSwitch: Switch | undefined;
    private voucherValueInput: NumberInput | undefined;
    private applyPriceAsCreditToFutureBillSwitch: Switch | undefined;

    private model?: IFullArticle;

    constructor(element: JQuery) {
        super(element, Factory.getInstance<CatalogDao>(CatalogDao).articleDataSource as any);

        this.availabilitySwitch = new Switch(this.$form.find('input[name="isavailable"]'));
        this.isOpenPriceEnabledSwitch = new Switch(this.$form.find('input[name="isopenpriceenabledswitch"]'));

        this.$isNegativeOpenPriceAllowedRow = this.$form.find(".is-negative-open-price-allowed-row");
        this.isNegativeOpenPriceAllowedSwitch = new Switch(
            this.$form.find('input[name="isNegativeOpenPriceAllowedSwitch"]')
        );
        this.pricePerMinuteSwitch = new Switch(this.$form.find('input[name="pricePerMinuteSwitch"]'));
        this.$timerAutoStartAtOperatorCheckInRow = this.$form.find(".timer-auto-start-at-operator-check-in-row");
        this.timerAutoStartAtOperatorCheckInSwitch = new Switch(
            this.$form.find('input[name="timerAutoStartAtOperatorCheckInSwitch"]')
        );
        this.$tablesRow = this.$form.find(".tables-row");
        this.tablesInput = MultiEntityInput.createFromInput(this.$form.find('.form-control[name="tables"]'), {
            dataSource: Factory.getInstance<TableDao>(TableDao).tableSearch,
            maxVisibleOptions: 10,
        });
        this.$continuationGroupsRow = this.$form.find(".continuation-groups-row");
        this.continuationGroupsInput = MultiTextInput.createFromInput(
            this.$form.find('.form-control[name="continuationGroups"]'),
            { suggestions: () => Factory.getInstance<CatalogDao>(CatalogDao).getFollowOnGroups() }
        );

        this.isOpenPriceEnabledSwitch.onChange = (isEnabled) => {
            this.priceInput.setDisabled(isEnabled, true);
            this.oohPriceInput.setDisabled(isEnabled, true);
            this.voucherValueInput?.setDisabled(isEnabled, true);
            this.setIsNegativeOpenPriceAllowedRowVisible(isEnabled);
        };

        this.pricePerMinuteSwitch.onChange = (isEnabled) => {
            this.updateQuantityInputsBasedOnPricePerMinute(isEnabled);
            this.setPricePerMinuteRowsVisible(isEnabled);
            this.setPriceDecimals(isEnabled);
        };

        this.nameField = MultiLanguageInput.fromInput(this.$form.find('.form-control[name="name"]'));
        this.nameField.onChange = (e) => {
            this.updateName(translate(e));
        };

        this.categoryInput = MultiEntityInput.createFromInput(this.$form.find('.form-control[name="categories"]'), {
            dataSource: Factory.getInstance<CatalogDao>(CatalogDao).categorySearch,
        });

        this.attributeInput = MultiEntityInput.createFromInput(this.$form.find('.form-control[name="attributes"]'), {
            dataSource: Factory.getInstance<CatalogDao>(CatalogDao).attributeSearch,
            cssClass: (m: IAttributeLinkableEntity) =>
                m.effect === AttributeEffect.Exclude ? "exclude-attribute" : undefined,
        });

        this.shortDescriptionField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="shortdescription"]')
        );

        this.longDescriptionField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="longdescription"]')
        );

        this.priceInput = new NumberInput(this.$form.find('.form-control[name="price"]'), {
            min: Number.MAX_SAFE_INTEGER * -1,
            step: 0.01,
            decimals: 2,
            hideButtons: true,
        });

        this.oohPriceInput = new NumberInput(this.$form.find('.form-control[name="oohprice"]'), {
            min: Number.MAX_SAFE_INTEGER * -1,
            step: 0.01,
            decimals: 2,
            hideButtons: true,
        });

        this.vatInput = new NumberInput(this.$form.find('.form-control[name="vat"]'), {
            min: 0,
            step: 0.01,
            decimals: 2,
            hideButtons: true,
        });

        this.oohVatInput = new NumberInput(this.$form.find('.form-control[name="oohvat"]'), {
            min: 0,
            step: 0.01,
            decimals: 2,
            hideButtons: true,
        });

        const quantityFieldInputConfig = {
            min: 1,
            max: 9999,
            step: 1,
            decimals: 0,
        };

        this.$minQuantityField = this.$form.find('.form-control[name="minQuantity"]');
        this.minQuantityInput = new NumberInput(
            this.$form.find('.form-control[name="minQuantity"]'),
            quantityFieldInputConfig
        );
        this.maxQuantityInput = new NumberInput(
            this.$form.find('.form-control[name="maxQuantity"]'),
            quantityFieldInputConfig
        );

        this.imagePicker = new ImagePicker(Factory.getInstance<CatalogDao>(CatalogDao).articleImageDataSource).renderTo(
            this.$form.find("#articleimage")
        );
        this.optionGroupAllocator = new OptionGroupAllocator().replaceWith(this.$form.find(".optiongroups"));

        const productionSitesSection = this.$form.find("#production-sites-section");
        if (productionSitesSection != null && productionSitesSection.length > 0) {
            this.articleProductionSiteAllocations = new ArticleProductionSiteAllocator().replaceWith(
                productionSitesSection
            );
        }

        if (this.$form.find("#uses-resources-section").length > 0) {
            this.articleResourcesForm = createVueComponent(
                ArticleResourcesForm,
                {},
                this.$form.find("#uses-resources-section")[0],
                true
            ).mountedComponent;
        }

        this.canHaveCommentsSwitch = new Switch(this.$form.find('input[name="canHaveComments"]'));

        this.minAgeInput = new NumberInput(this.$form.find('.form-control[name="minage"]'), {
            min: 1,
            max: 99,
            step: 1,
            decimals: 0,
        });

        const reportCategorySection = this.$form.find("#reporting-category-section");
        if (reportCategorySection != null && reportCategorySection.length > 0) {
            this.reportCategorySelect = Select.createFromSelect(
                this.$form.find('.form-control[name="reportcategory"]'),
                Factory.getInstance<CatalogDao>(CatalogDao).reportCategoryDataSource,
                (c) => c.name,
                false,
                i18n("item.noCategory")
            );
        }

        const fulfillmentGroupSection = this.$form.find("#fulfillment-group-section");
        if (fulfillmentGroupSection != null && fulfillmentGroupSection.length > 0) {
            this.fulfillmentGroupSelect = Select.createFromSelect(
                this.$form.find('.form-control[name="fulfillmentgroup"]'),
                Factory.getInstance<CatalogDao>(CatalogDao).fulfillmentGroupDataSource,
                (c) => (c.internalName === undefined || c.internalName === null ? translate(c.name) : c.internalName),
                false,
                i18n("item.noFulfillmentGroup"),
                undefined,
                undefined,
                undefined,
                (items) => {
                    return items
                        .sort((item1, item2) => {
                            const item1Name = translate(item1.name);
                            const item2Name = translate(item2.name);
                            return item1Name > item2Name ? 1 : -1;
                        })
                        .sort((item1, item2) => {
                            const order1 = item1.order ?? 999999999;
                            const order2 = item2.order ?? 999999999;

                            if (order1 === order2) {
                                return 0;
                            }

                            if (order1 > order2) return 1;

                            return -1;
                        });
                }
            );
        }

        this.$internalNameField = this.$form.find('.form-control[name="internalname"]');
        this.$internalNoteField = this.$form.find('.form-control[name="internalnote"]');

        this.$voucherValueSection = this.$form.find("#voucher-value-section");
        if (this.$voucherValueSection != null && this.$voucherValueSection.length > 0) {
            this.isValueVoucherSwitch = new Switch(this.$form.find('input[name="isValueVoucher"]'));
            this.isValueVoucherSwitch.onChange = () => {
                this.updateVoucherValueSection();
                this.updateReportCategoryBasedOnValueVoucher();
                this.updateVatBasedOnValueVoucher();
            };

            const voucherValueInput = this.$form.find('.form-control[name="voucherValue"]');
            if (voucherValueInput.length > 0) {
                this.voucherValueInput = new NumberInput(voucherValueInput, {
                    min: 0,
                    step: 0.01,
                    decimals: 2,
                });
            }
        }


        const applyPriceAsCreditToFutureBillSwitch  = this.$form.find(
            'input[name="applyPriceAsCreditToFutureBillCheckbox"]'
        );

        if (applyPriceAsCreditToFutureBillSwitch != null && applyPriceAsCreditToFutureBillSwitch.length > 0) {
            this.applyPriceAsCreditToFutureBillSwitch = new Switch(applyPriceAsCreditToFutureBillSwitch);
        }

    }

    public setFormData(model?: IFullArticle) {
        this.model = model;
        this.updateName(model === undefined ? "" : translate(model.name));
        this.availabilitySwitch.setValue(model == null ? false : model.isAvailable);
        this.nameField.setValues(model === undefined ? undefined : model.name);
        this.shortDescriptionField.setValues(model === undefined ? undefined : model.shortDescription);
        this.longDescriptionField.setValues(model === undefined ? undefined : model.longDescription);
        this.categoryInput.setValue(
            model === undefined
                ? undefined
                : model.offerCategories.map((c) => {
                      return {
                          id: c.id,
                          name: translate(c.name),
                          appearsIn: c.offers.map((d) => {
                              return { id: d.id, name: d.name };
                          }),
                      };
                  })
        );
        this.attributeInput.setValue(
            model === undefined
                ? undefined
                : model.attributes.map((a): IAttributeLinkableEntity => {
                      return { id: a.attributeId, name: translate(a.name), effect: a.effect };
                  })
        );
        // before the prices, which the inputs then show with the decimals of the article
        this.setPriceDecimals(model?.isPricePerMinute ?? false);
        this.priceInput.setValue(model === undefined ? 0 : model.price);
        this.oohPriceInput.setValue(model === undefined || model.oohPrice === undefined ? undefined : model.oohPrice);
        this.vatInput.setValue(model === undefined || model.vat === undefined ? undefined : model.vat * 100);
        this.oohVatInput.setValue(model === undefined || model.oohVat === undefined ? undefined : model.oohVat * 100);
        this.minQuantityInput.setValue(
            model === undefined || model.minQuantity === undefined ? undefined : model.minQuantity
        );
        this.maxQuantityInput.setValue(
            model === undefined || model.maxQuantity === undefined ? undefined : model.maxQuantity
        );
        this.optionGroupAllocator.setValue(model === undefined ? undefined : model.optionGroups);
        this.canHaveCommentsSwitch.setValue(
            model === undefined || model.canHaveComments === undefined ? false : model.canHaveComments
        );
        this.minAgeInput.setValue(model === undefined || model.minAge === undefined ? undefined : model.minAge);
        this.isOpenPriceEnabledSwitch.setValue(model == null ? false : model.isOpenPriceEnabled);
        this.isNegativeOpenPriceAllowedSwitch.setValue(model == null ? false : model.isNegativeOpenPrice);
        this.pricePerMinuteSwitch.setValue(model == null ? false : model.isPricePerMinute);
        this.timerAutoStartAtOperatorCheckInSwitch.setValue(model?.isTimerAutoStartAtOperatorCheckIn ?? false);
        this.continuationGroupsInput.setValue(model?.followOnGroups ?? []);
        this.setTables(model?.displayOnlyOnTableIds ?? []);

        if (this.reportCategorySelect != null) {
            this.reportCategorySelect.setValue(model === undefined ? undefined : model.reportCategoryId);
        }

        if (this.fulfillmentGroupSelect != null) {
            this.fulfillmentGroupSelect.setValue(model === undefined ? undefined : model.fulfillmentGroupId);
        }

        this.$internalNameField.val(model === undefined || model.internalName === undefined ? "" : model.internalName);
        this.$internalNoteField.val(model === undefined || model.internalNote === undefined ? "" : model.internalNote);
        this.imagePicker.setValue(model);

        if (this.articleProductionSiteAllocations != null && model != null) {
            this.articleProductionSiteAllocations.setValue(model!.productionSites);
        }

        this.isValueVoucherSwitch?.setValue(model?.isValueVoucher ?? false);
        this.voucherValueInput?.setValue(model?.voucherValue?.toFixed(2));

        this.articleResourcesForm?.setData(model?.resources ?? []);
        this.applyPriceAsCreditToFutureBillSwitch?.setValue(model?.applyPriceAsCreditToFutureBill ?? false);

        this.updateVoucherValueSection();
        this.updateReportCategoryBasedOnValueVoucher();
        this.updateVatBasedOnValueVoucher();
        this.setIsNegativeOpenPriceAllowedRowVisible(model?.isOpenPriceEnabled ?? false);
        this.updateQuantityInputsBasedOnPricePerMinute(model?.isPricePerMinute ?? false);
        this.setPricePerMinuteRowsVisible(model?.isPricePerMinute ?? false);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreateArticleCmd {
        const getValidatedArticleResourcesResult = this.articleResourcesForm?.getValidatedData();

        const data: ICreateArticleCmd = {
            isAvailable: this.availabilitySwitch.getValue(),
            name: this.nameField.getValues(),
            shortDescription: this.shortDescriptionField.getValues(),
            longDescription: this.longDescriptionField.getValues(),
            categoryIds: this.categoryInput.getValue(),
            attributeIds: this.attributeInput.getValue(),
            price: this.priceInput.getValue()!,
            oohPrice: this.oohPriceInput.getValue() === undefined ? undefined : this.oohPriceInput.getValue(),
            vat:
                this.vatInput.getValue() === undefined
                    ? undefined
                    : Math.round(this.vatInput.getValue()! * 1000) / 100000,
            oohVat:
                this.oohVatInput.getValue() === undefined
                    ? undefined
                    : Math.round(this.oohVatInput.getValue()! * 1000) / 100000,
            minAge: this.minAgeInput.getValue(),
            productionSiteAllocations: this.articleProductionSiteAllocations?.getValues(),
            reportCategoryId: this.reportCategorySelect?.getValue(),
            fulfillmentGroupId: this.fulfillmentGroupSelect?.getValue(),
            optionGroups: this.optionGroupAllocator.getValue(),
            internalName: this.$internalNameField.val().length === 0 ? undefined : this.$internalNameField.val(),
            internalNote: this.$internalNoteField.val().length === 0 ? undefined : this.$internalNoteField.val(),
            minQuantity: this.minQuantityInput.getValue(),
            maxQuantity: this.maxQuantityInput.getValue(),
            isOpenPriceEnabled: this.isOpenPriceEnabledSwitch.getValue(),
            isNegativeOpenPrice: this.isNegativeOpenPriceAllowedSwitch.getValue(),
            isPricePerMinute: this.pricePerMinuteSwitch.getValue(),
            isTimerAutoStartAtOperatorCheckIn: this.pricePerMinuteSwitch.getValue()
                ? this.timerAutoStartAtOperatorCheckInSwitch.getValue()
                : undefined,
            followOnGroups: this.pricePerMinuteSwitch.getValue() ? this.continuationGroupsInput.getValue() : undefined,
            displayOnlyOnTableIds: this.pricePerMinuteSwitch.getValue() ? this.getTableIds() : undefined,
            isValueVoucher:
                this.isValueVoucherSwitch != null
                    ? this.isValueVoucherSwitch.getValue()
                    : (this.model?.isValueVoucher ?? false),
            voucherValue: this.voucherValueInput != null ? this.voucherValueInput.getValue() : this.model?.voucherValue,
            canHaveComments: this.canHaveCommentsSwitch.getValue(),
            resources:
                this.articleResourcesForm != null
                    ? getValidatedArticleResourcesResult!.articleResources
                    : (this.model?.resources ?? []),
            applyPriceAsCreditToFutureBill:
                this.applyPriceAsCreditToFutureBillSwitch != null
                    ? this.applyPriceAsCreditToFutureBillSwitch.getValue()
                    : (this.model?.applyPriceAsCreditToFutureBill ?? false),
            idempotencyKey: this.id === undefined ? this.idempotencyKey : undefined,
        };

        let hasError = false;

        if (this.articleResourcesForm != null && !getValidatedArticleResourcesResult!.isValid) {
            hasError = true;
        }

        if (
            data.name.de === undefined &&
            data.name.en === undefined &&
            data.name.fr === undefined &&
            data.name.it === undefined
        ) {
            hasError = true;
            this.nameField.setError(true);
        } else {
            if (
                (data.name.de !== undefined && data.name.de.length > 80) ||
                (data.name.fr !== undefined && data.name.fr.length > 80) ||
                (data.name.it !== undefined && data.name.it.length > 80) ||
                (data.name.en !== undefined && data.name.en.length > 80)
            ) {
                hasError = true;
                this.nameField.setError(true);
            } else {
                this.nameField.setError(false);
            }
        }

        // Validate production sites
        const productionSitesValid = this.articleProductionSiteAllocations?.validateRows() ?? true;
        if (!productionSitesValid) {
            hasError = true;
        }

        if (data.shortDescription !== undefined) {
            if (
                (data.shortDescription.de !== undefined && data.shortDescription.de.length > 200) ||
                (data.shortDescription.fr !== undefined && data.shortDescription.fr.length > 200) ||
                (data.shortDescription.it !== undefined && data.shortDescription.it.length > 200) ||
                (data.shortDescription.en !== undefined && data.shortDescription.en.length > 200)
            ) {
                hasError = true;
                this.shortDescriptionField.setError(true);
            } else {
                this.shortDescriptionField.setError(false);
            }
        }

        if (data.longDescription !== undefined) {
            if (
                (data.longDescription.de !== undefined && data.longDescription.de.length > 500) ||
                (data.longDescription.fr !== undefined && data.longDescription.fr.length > 500) ||
                (data.longDescription.it !== undefined && data.longDescription.it.length > 500) ||
                (data.longDescription.en !== undefined && data.longDescription.en.length > 500)
            ) {
                hasError = true;
                this.longDescriptionField.setError(true);
            } else {
                this.longDescriptionField.setError(false);
            }
        }

        if (data.internalName !== undefined && data.internalName.length > 80) {
            hasError = true;
            this.$internalNameField.addClass("is-invalid");
        }

        if (data.price === undefined || isNaN(data.price)) {
            hasError = true;
            this.priceInput.setError(true);
        } else this.priceInput.setError(false);

        if (data.oohPrice !== undefined && (isNaN(data.oohPrice))) {
            hasError = true;
            this.oohPriceInput.setError(true);
        } else this.oohPriceInput.setError(false);

        if (data.minAge !== undefined && (data.minAge > 99 || data.minAge < 1)) {
            hasError = true;
            this.minAgeInput.setError(true);
        } else this.minAgeInput.setError(false);

        let hasMinQuantityError = false;
        if (data.minQuantity !== undefined && (data.minQuantity > 9999 || data.minQuantity < 1)) {
            hasMinQuantityError = true;
        }

        let hasMaxQuantityError = false;
        if (data.maxQuantity !== undefined && (data.maxQuantity > 9999 || data.maxQuantity < 1)) {
            hasMaxQuantityError = true;
        }

        if (data.minQuantity !== undefined && data.maxQuantity !== undefined && data.minQuantity > data.maxQuantity) {
            hasMinQuantityError = true;
            hasMaxQuantityError = true;
        }

        if (hasMinQuantityError || hasMaxQuantityError) {
            hasError = true;
            this.minQuantityInput.setError(hasMinQuantityError);
            this.maxQuantityInput.setError(hasMaxQuantityError);
        }

        // A price per minute item has to be a main item, so it needs at least one offer category
        if (data.isPricePerMinute && data.categoryIds.length === 0) {
            hasError = true;
            this.categoryInput.setError(true);
        } else this.categoryInput.setError(false);

        this.voucherValueInput?.setError(false);
        if (this.voucherValueInput != null && data.isValueVoucher) {
            if (data.voucherValue == null) {
                hasError = true;
                this.voucherValueInput!.setError(true);
            }

            if (data.reportCategoryId == null) {
                hasError = true;
                this.reportCategorySelect?.setError(true);
            }
        }

        if (hasError) {
            this.scrollToFirstErrorField();
        }

        return (hasError ? undefined : data) as any;
    }

    private setIsNegativeOpenPriceAllowedRowVisible(show: boolean) {
        if (show) {
            this.$isNegativeOpenPriceAllowedRow.show();
        } else {
            this.$isNegativeOpenPriceAllowedRow.hide();
        }
    }

    private getTableIds(): Array<string> {
        return this.tablesLoaded ? this.tablesInput.getValue() : this.savedTableIds;
    }

    private setTables(tableIds: Array<string>) {
        this.savedTableIds = tableIds;

        if (tableIds.length === 0) {
            this.tablesLoaded = true;
            this.tablesInput.setValue([]);
            return;
        }

        this.tablesLoaded = false;
        Factory.getInstance<TableDao>(TableDao)
            .tableSearch.search([])
            .then((tables) => {
                // tables that do not exist anymore are dropped
                this.tablesInput.setValue(tables.filter((table) => tableIds.includes(table.id)));
                this.tablesLoaded = true;
            })
            .catch((ex) => logError(ex));
    }

    private setPricePerMinuteRowsVisible(show: boolean) {
        if (show) {
            this.$timerAutoStartAtOperatorCheckInRow.show();
            this.$continuationGroupsRow.show();
            this.$tablesRow.show();
        } else {
            this.$timerAutoStartAtOperatorCheckInRow.hide();
            this.$continuationGroupsRow.hide();
            this.$tablesRow.hide();
        }
    }

    // The backend forces min quantity to none and max quantity to 1 for a price per minute article
    private updateQuantityInputsBasedOnPricePerMinute(isPricePerMinute: boolean) {
        if (isPricePerMinute) {
            this.minQuantityInput.setValue(undefined);
            this.maxQuantityInput.setValue(1);
            this.minQuantityInput.setError(false);
            this.maxQuantityInput.setError(false);
        }

        this.minQuantityInput.setDisabled(isPricePerMinute);
        this.maxQuantityInput.setDisabled(isPricePerMinute);
    }

    // A price per minute is a rate with up to 5 decimals (28 CHF per hour is 0.46667), every other price is in cents. The
    // backend rejects more decimals than that
    private setPriceDecimals(isPricePerMinute: boolean) {
        const decimals = isPricePerMinute ? 5 : 2;
        this.priceInput.setDecimals(decimals);
        this.oohPriceInput.setDecimals(decimals);
    }

    public onPostSave(article: IArticle): Promise<IArticle> {
        return this.imagePicker.handle(article);
    }

    protected confirmSave(data: ICreateArticleCmd): Promise<boolean> {
        return BeforeSaveValidation.confirm(
            Factory.getInstance<CatalogDao>(CatalogDao).validateArticleBeforeSave({
                articleId: this.id,
                name: data.name,
                shortDescription: data.shortDescription,
                longDescription: data.longDescription,
            }),
            this.$form.find('.form-control[name="name"]')
        );
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case exceptionTypes.missingStandardServiceDurationForSeatsException:
                return i18n("item.missingStandardServiceDurationForSeats");
            case "article_exists_in_cart_exception":
                return i18n("item.itemDeletionFailed");
            case exceptionTypes.articleActivationInsufficientStockException:
                return i18n("item.insufficientStock");
            case exceptionTypes.articleCannotBeOpenPriceAndPricePerMinuteException:
                return i18n("item.openPriceAndPricePerMinuteExclusive");
            case "article_min_quantity_greater_than_option_group_max_quantity_exception":
                this.minQuantityInput.setError(true);
                DomUtils.scrollTo(this.$minQuantityField);

                return i18n("item.articleMinQuantityGreaterThanOptionGroupMaxPerOption", {
                    article: b.additionalData.article,
                    articleMinQuantity: b.additionalData.articleMinQuantity,
                    optionGroup: b.additionalData.optionGroup,
                    optionGroupMaxPerQuantity: b.additionalData.optionGroupMaxPerQuantity,
                });
        }
        return b.additionalData.reason;
    }

    private updateVoucherValueSection() {
        if (this.voucherValueInput == null || this.isValueVoucherSwitch == null)
            return;

        const isValueVoucher = this.isValueVoucherSwitch.getValue();

        if (isValueVoucher) {
            this.$voucherValueSection.show();

            if (this.voucherValueInput.getValue() == null) {
                const price = this.priceInput.getValue();
                this.voucherValueInput.setValue(this.model?.voucherValue ?? price);
            }
        } else {
            this.$voucherValueSection.hide();
            this.voucherValueInput.setValue(undefined);
        }
    }

    private updateVatBasedOnValueVoucher() {

        const isValueVoucher = this.isValueVoucherSwitch?.getValue() ?? this.model?.isValueVoucher ?? false;
        if (isValueVoucher) {
            this.vatInput!.setValue(0);
            this.oohVatInput!.setValue(0);
        } else {
            this.vatInput!.setValue(this.model?.vat == null ? undefined : this.model.vat * 100);
            this.oohVatInput!.setValue(this.model?.oohVat == null ? undefined : this.model?.oohVat * 100);
        }

        this.vatInput!.setDisabled(isValueVoucher);
        this.oohVatInput!.setDisabled(isValueVoucher);
    }

    private updateReportCategoryBasedOnValueVoucher() {
        if (this.reportCategorySelect == null && this.isValueVoucherSwitch == null)
            return;

        const isValueVoucher = this.isValueVoucherSwitch!.getValue();
        this.reportCategorySelect?.setDisabled(isValueVoucher);
        const entries = this.reportCategorySelect!.getEntries();

        if (isValueVoucher) {
            if (entries.length > 0) {
                const salesEntry = entries?.find((e) => e.name === this.valueVoucherSalesReportCategoryName);
                this.reportCategorySelect!.setValue(salesEntry?.id);
            }
        } else {
            if (entries.length > 0) {
                const entry = entries?.find((e) => e.id === this.model?.reportCategoryId);
                this.reportCategorySelect!.setValue(
                    entry?.name !== this.valueVoucherSalesReportCategoryName ? this.model?.reportCategoryId : undefined
                );
            } else {
                this.reportCategorySelect!.setValue(this.model?.reportCategoryId);
            }
        }
    }
}
